import React, { useEffect, useState, useCallback } from "react";
import { Star, Check, X, Trash2, RefreshCw } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

export default function ReviewsPage() {
  const { authHeaders } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState("pending"); // pending | approved | all

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = filter === "all" ? "" : `?approved=${filter === "approved"}`;
      const res  = await fetch(`${API}/reviews${qs}`, { headers: authHeaders() });
      const json = await res.json();
      setReviews(json.data || []);
    } catch {}
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  async function setApproved(id, approved) {
    const res = await fetch(`${API}/reviews/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ approved }),
    });
    if (res.ok) setReviews(prev => prev.filter(r => r._id !== id));
  }

  async function remove(id) {
    const res = await fetch(`${API}/reviews/${id}`, { method: "DELETE", headers: authHeaders() });
    if (res.ok) setReviews(prev => prev.filter(r => r._id !== id));
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reviews</h1>
          <p className="text-sm text-gray-500">Moderate guest reviews before they go live</p>
        </div>
        <button onClick={load} className="tap-44 p-2 hover:bg-gray-100 rounded-xl transition-colors">
          <RefreshCw size={16} className={`text-gray-500 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      <div className="flex gap-2">
        {[["pending", "Pending"], ["approved", "Approved"], ["all", "All"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)}
            className={`tap-44 text-sm font-medium px-3 py-1.5 rounded-xl border transition-colors
              ${filter === v ? "bg-amber-500 text-white border-amber-500" : "bg-white text-gray-600 border-gray-200"}`}>
            {l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-400"><RefreshCw size={20} className="animate-spin mx-auto mb-2" /> Loading…</div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-16 text-gray-400">No reviews here.</div>
      ) : (
        <div className="space-y-3">
          {reviews.map(r => (
            <div key={r._id} className="bg-white rounded-2xl border border-gray-100 p-5 flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-bold text-gray-900">{r.guestName}</p>
                  <div className="flex items-center gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} size={12} className={i < r.rating ? "text-amber-500 fill-amber-500" : "text-gray-200"} />
                    ))}
                  </div>
                  {r.approved && <span className="text-[10px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Live</span>}
                </div>
                <p className="text-xs text-gray-400 mb-2">
                  {r.room?.name ? `${r.room.name} · ` : ""}{r.booking?.bookingRef} · {new Date(r.createdAt).toLocaleDateString("en-IN")}
                </p>
                {r.comment && <p className="text-sm text-gray-600">{r.comment}</p>}
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {!r.approved && (
                  <button onClick={() => setApproved(r._id, true)}
                    className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 font-medium">
                    <Check size={12} /> Approve
                  </button>
                )}
                {r.approved && (
                  <button onClick={() => setApproved(r._id, false)}
                    className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 font-medium">
                    <X size={12} /> Unpublish
                  </button>
                )}
                <button onClick={() => remove(r._id)}
                  className="tap-44 p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100">
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
